import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { BlockTypes } from "../../models/block-types";
import {
  GameState,
  getNextCanvasState,
  initialGameState,
  ensureNodeBlockTypes,
} from "../../models/game";
import { Block, BlockMouseDownEvent, BlockPositionMap, ScreenPosition, blockConfigs, zeroIOPS, ROOT_BLOCK_ID, IOPS } from "../../models/block";
import { TrafficPatterns, TrafficRoutes } from "../../models/traffic-patterns";
import { getProblemById, getProblemInitialIOPS } from "../../models/problems";
import {
  ConnectPointClickEvent,
  ConnectorMap,
  ConnectorTypes,
  ReactFlowHandleClickEvent,
} from "../../models/connectors";
import { Connection, EdgeChange, Node, NodeChange, addEdge, applyEdgeChanges, applyNodeChanges, getConnectedEdges } from "reactflow";

// Define the initial state using that type
const initialState: GameState = initialGameState;

export const gameSlice = createSlice({
  name: "game",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    gameStart: (state) => {
      state.isStarted = true;
      state.isRunning = true;
      // Apply the configured initial IOPS to the root block
      if (state.blockIOPS[ROOT_BLOCK_ID]) {
        state.blockIOPS[ROOT_BLOCK_ID].outputIOPS = state.initialRootIOPS;
      }
    },
    gamePause: (state) => {
      state.isRunning = false;
    },
    gameResume: (state) => {
      state.isRunning = true;
    },
    gameTick: (state, action: PayloadAction<number>) => {
      state.timeElapsedInSec += action.payload;
      const nextGameState = getNextCanvasState(state);
      state.blockIOPS = nextGameState.blockIOPS;
      state.level = nextGameState.level;
      state.accountBalance = nextGameState.accountBalance;
      state.blocks = nextGameState.blocks;
    },
    blockMouseDown: (state, action: PayloadAction<BlockMouseDownEvent>) => {
      state.draggingBlock = action.payload;
      state.blocks[action.payload.blockID].isSelected = true;

      //De-select the previous block if it is not the same as the current one
      if (state.selectedBlock && state.selectedBlock !== action.payload.blockID) {
        state.blocks[state.selectedBlock].isSelected = false;
      }

      state.selectedBlock = action.payload.blockID;
    },
    blockMouseUp: (state) => {
      state.draggingBlock = null;
    },
    canvasClick: (state) => {
      //De-select the currently selected block, if any
      if (state.selectedBlock) {
        state.blocks[state.selectedBlock].isSelected = false;
      }
      state.selectedBlock = null;
    },
    blockMove: (state, action: PayloadAction<ScreenPosition>) => {
      if (state.draggingBlock) {
        const newTop = action.payload.top - state.draggingBlock.diff.top;
        const newLeft = action.payload.left - state.draggingBlock.diff.left;

        state.blocks[state.draggingBlock.blockID].top = newTop;
        state.blocks[state.draggingBlock.blockID].left = newLeft;
      }
    },
    addBlock: (state, action: PayloadAction<Block>) => {
      let newBlock = action.payload;
      newBlock.id = generateNewKey(state);

      const prevCount = Object.values(state.blocks).filter(b => b.blockType === newBlock.blockType).length;

      const newLabel = newBlock.blockType === BlockTypes.TEXT_BOX
        ? "Double-click to type..."
        : `${blockConfigs[action.payload.blockType].displayName} ${prevCount + 1}`;
      newBlock.customName = newLabel;

      if (newBlock.blockType === BlockTypes.TEXT_BOX) {
        newBlock.width = 160;
        newBlock.height = 80;
      }

      if (newBlock.blockType === BlockTypes.ARROW) {
        newBlock.height = 25;
        newBlock.width = 500;
      }

      state.blocks = {
        ...state.blocks,
        [newBlock.id]: newBlock,
      };
      state.blockIOPS[newBlock.id] = {
        blockID: newBlock.id, inputIOPS: zeroIOPS(), outputIOPS: zeroIOPS(), percentageSuccess: 100
      };
      const purchasePrice = blockConfigs[state.blocks[newBlock.id].blockType].purchasePrice;
      state.accountBalance -= purchasePrice;
      state.connectionStartBlock = null;
      const newNode: Node = {
        id: newBlock.id,
        position: { x: action.payload.left, y: action.payload.top },
        type: 'gameBlock',
        data: {
          label: newLabel,
          blockType: action.payload.blockType
        },
        width: newBlock.width,
        height: newBlock.height,
      };
      state.nodes.push(newNode);
    },
    removeSelectedBlock: (state, action: PayloadAction<string>) => {
      if (state.isStarted) {
        const sellPrice = blockConfigs[state.blocks[action.payload].blockType].sellPrice;
        state.accountBalance += sellPrice;
      }
      else {
        const purchasePrice = blockConfigs[state.blocks[action.payload].blockType].purchasePrice;
        state.accountBalance += purchasePrice;
      }

      let newCanvasState = cleanupRelatedConnections(action.payload, state);
      let finalBlocks: BlockPositionMap = {};
      Object.values(newCanvasState.blocks).forEach((block) => {
        if (block.id !== action.payload) {
          finalBlocks = {
            ...finalBlocks,
            [block.id]: block,
          };
        }
      });

      delete newCanvasState.blockIOPS[action.payload];

      state.blocks = finalBlocks;
      state.connectors = newCanvasState.connectors;
      state.selectedBlock = null;
      state.connectionStartBlock = null;

      const nodesToDelete = [...state.nodes.filter((node) => node.id === action.payload)];
      const edgesToDelete = new Set(getConnectedEdges(nodesToDelete, state.edges).map((edge) => edge.id));
      state.edges = state.edges.filter((edge) => !edgesToDelete.has(edge.id));
      state.nodes = state.nodes.filter((node) => node.id !== action.payload);

    },
    removeSelectedEdge: (state, action: PayloadAction<string>) => {
      state.edges = state.edges.filter((edge) => edge.id !== action.payload);
    },
    connectBlock: (state, action: PayloadAction<ConnectPointClickEvent>) => {
      //if this is the first connection point clicked
      //save it for later when the second one is clicked
      if (!state.connectionStartBlock) {
        state.connectionStartBlock = action.payload;
        return;
      }

      //make sure the start block is at the input of the connector
      //end the end block is at the output
      let endBlockID = state.connectionStartBlock.blockID;
      let startBlockID = action.payload.blockID;

      if (action.payload.connectorType === ConnectorTypes.INPUT) {
        endBlockID = action.payload.blockID;
        startBlockID = state.connectionStartBlock.blockID;
      }

      // Create a new connector and link it to the blocks
      const connectorId = generateNewKey(state);

      const inputConnections =
        state.blocks[endBlockID].inputConnectors || []

      inputConnections.push(connectorId);

      const outputConnections =
        state.blocks[startBlockID].outputConnectors || []

      outputConnections.push(connectorId);

      state.connectors[connectorId] = {
        id: connectorId,
        inputBlockKey: startBlockID,
        outputBlockKey: endBlockID,
      };

      state.blocks = {
        ...state.blocks,
        [startBlockID]: {
          ...state.blocks[startBlockID],
          outputConnectors: outputConnections,
        },
        [endBlockID]: {
          ...state.blocks[endBlockID],
          inputConnectors: inputConnections,
        },
      };

      //reset connection start block for the next connection event
      state.connectionStartBlock = null;
    },
    nodesChanged: (state, action: PayloadAction<NodeChange[]>) => {
      state.nodes = applyNodeChanges(action.payload, state.nodes);

      action.payload.forEach((change) => {
        if (change.type === "dimensions" && change.dimensions) {
          const block = state.blocks[change.id];
          if (block && (block.blockType === BlockTypes.ARROW || block.blockType === BlockTypes.TEXT_BOX)) {
            block.width = change.dimensions.width;
            block.height = change.dimensions.height;
          }
        }
        if (change.type === "position" && change.position) {
          if (state.blocks[change.id]) {
            state.blocks[change.id].left = change.position.x;
            state.blocks[change.id].top = change.position.y;
          }
        }
      });
    },
    edgesChanged: (state, action: PayloadAction<EdgeChange[]>) => {
      state.edges = applyEdgeChanges(action.payload, state.edges);
    },
    onConnect: (state, action: PayloadAction<Connection>) => {
      const edge = { ...action.payload, type: 'gameEdge' };
      state.edges = addEdge(edge, state.edges);
      state.connectionStartNode = null;
    },
    nodeHandleClick: (state, action: PayloadAction<ReactFlowHandleClickEvent>) => {
      if (!state.connectionStartNode) {
        state.connectionStartNode = action.payload;
      }
    },
    connectEnd: (state) => {
      state.connectionStartNode = null;
    },
    updateInitialRootIOPS: (state, action: PayloadAction<IOPS>) => {
      state.initialRootIOPS = action.payload;
    },
    updateCurrentRootIOPS: (state, action: PayloadAction<IOPS>) => {
      if (state.blockIOPS[ROOT_BLOCK_ID]) {
        state.blockIOPS[ROOT_BLOCK_ID].outputIOPS = action.payload;
      }
    },
    updateTrafficPattern: (state, action: PayloadAction<TrafficPatterns>) => {
      state.trafficPattern = action.payload;
    },
    updateTrafficRoutes: (state, action: PayloadAction<TrafficRoutes>) => {
      state.trafficRoutes = action.payload;
    },
    updateMinTPS: (state, action: PayloadAction<number>) => {
      state.minTPS = action.payload;
    },
    updateMaxTPS: (state, action: PayloadAction<number>) => {
      state.maxTPS = action.payload;
    },
    renameBlock: (state, action: PayloadAction<{ blockId: string; name: string }>) => {
      const { blockId, name } = action.payload;
      if (state.blocks[blockId]) {
        state.blocks[blockId].customName = name;
      }
      const node = state.nodes.find((n) => n.id === blockId);
      if (node) {
        node.data = { ...node.data, label: name };
      }
    },
    openBlockSettings: (state, action: PayloadAction<string>) => {
      state.settingsModalOpenForBlockId = action.payload;
    },
    closeBlockSettings: (state) => {
      state.settingsModalOpenForBlockId = null;
    },
    updateBlockDynamicProperties: (state, action: PayloadAction<{ blockId: string; properties: any }>) => {
      const { blockId, properties } = action.payload;
      if (state.blocks[blockId]) {
        state.blocks[blockId].dynamicProperties = {
          ...state.blocks[blockId].dynamicProperties,
          ...properties
        };
      }
    },
    resetGame: () => buildFreshGameState(),
    startProblem: (state, action: PayloadAction<string>) => {
      const problem = getProblemById(action.payload);
      if (!problem) return state;
      const fresh = buildFreshGameState();
      const initialRootIOPS = getProblemInitialIOPS(problem.traffic);
      return {
        ...fresh,
        trafficPattern: problem.traffic.pattern,
        // Copy so Immer never freezes the shared problem definitions
        trafficRoutes: JSON.parse(JSON.stringify(problem.traffic.routes)),
        minTPS: problem.traffic.minTPS,
        maxTPS: problem.traffic.maxTPS,
        initialRootIOPS,
        blockIOPS: {
          ...fresh.blockIOPS,
          [ROOT_BLOCK_ID]: { ...fresh.blockIOPS[ROOT_BLOCK_ID], outputIOPS: initialRootIOPS },
        },
        activeProblemId: problem.id,
      };
    },
    leaveProblem: (state) => {
      state.activeProblemId = null;
    },
    softResetGame: (state) => {
      state.timeElapsedInSec = 0;
      state.level = 1;
      state.accountBalance = initialGameState.accountBalance;
      state.isStarted = false;
      state.isRunning = false;
      state.isOver = false;

      // Reset block IOPS for all existing blocks
      const newBlockIOPS: typeof state.blockIOPS = {};
      Object.keys(state.blocks).forEach((blockId) => {
        newBlockIOPS[blockId] = {
          blockID: blockId,
          inputIOPS: zeroIOPS(),
          outputIOPS: blockId === ROOT_BLOCK_ID ? { ...state.initialRootIOPS } : zeroIOPS(),
          percentageSuccess: 100,
        };
      });
      state.blockIOPS = newBlockIOPS;

      // Reset queueState for any queue blocks
      Object.keys(state.blocks).forEach((blockId) => {
        const block = state.blocks[blockId];
        if (block.blockType === BlockTypes.QUEUE && block.dynamicProperties) {
          state.blocks[blockId] = {
            ...block,
            dynamicProperties: {
              ...block.dynamicProperties,
              queueState: zeroIOPS(),
            },
          };
        }
      });
    },
    loadDesignState: (_state, action: PayloadAction<GameState>) => {
      return ensureNodeBlockTypes({ ...action.payload });
    },
    setDraggingNode: (state, action: PayloadAction<boolean>) => {
      state.isDraggingNode = action.payload;
    },
    setIopsConfigModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isIopsConfigModalOpen = action.payload;
    },
    selectBlockForTour: (state, action: PayloadAction<string | null>) => {
      state.selectedBlock = action.payload;
      state.nodes = state.nodes.map((node) => ({
        ...node,
        selected: node.id === action.payload,
      }));
      Object.keys(state.blocks).forEach((blockId) => {
        if (state.blocks[blockId]) {
          state.blocks[blockId].isSelected = blockId === action.payload;
        }
      });
    },
    setPaletteCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isPaletteCollapsed = action.payload;
    },
  },
});

function buildFreshGameState(): GameState {
  const initialX = typeof window !== 'undefined' ? window.innerWidth / 2 - 50 : 500;
  const initialY = typeof window !== 'undefined' ? window.innerHeight / 2 - 50 : 300;
  return {
    ...initialGameState,
    nodes: [{ id: 'root', type: 'gameBlock', data: { label: 'User', blockType: BlockTypes.USER }, position: { x: initialX, y: initialY } }],
    blocks: {
      [`${ROOT_BLOCK_ID}`]: {
        top: initialY,
        left: initialX,
        id: ROOT_BLOCK_ID,
        width: 100,
        height: 100,
        blockType: BlockTypes.USER,
        inputCapacity: blockConfigs[BlockTypes.USER].inputCapacity,
        processingCapacity: blockConfigs[BlockTypes.USER].processingCapacity,
      },
    },
    blockIOPS: {
      ...initialGameState.blockIOPS,
    }
  };
}

function generateNewKey(state: GameState) {
  let key = Math.random().toString(20).substring(2);
  while (state.blocks[key] || state.connectors[key]) {
    key = Math.random().toString(20).substring(2);
  }
  return key;
}

const deleteConnection = (
  connectionID: string,
  connectionMap: ConnectorMap
): ConnectorMap => {
  let result: ConnectorMap = {};
  Object.values(connectionMap).forEach((conn) => {
    if (conn.id !== connectionID) {
      result = { ...result, [conn.id]: conn };
    }
  });
  return result;
};

const cleanupRelatedConnections = (
  blockID: string,
  oldCanvasState: GameState
): GameState => {
  let newCanvasState = { ...oldCanvasState };
  const otherBlock = newCanvasState.blocks[blockID];
  if (otherBlock.inputConnectors) {
    Array.from(otherBlock.inputConnectors).forEach((connID) => {
      const otherBlockID = newCanvasState.connectors[connID].inputBlockKey;

      let otherOutputConnectors = [...newCanvasState.blocks[otherBlockID].outputConnectors || []]
      otherOutputConnectors = otherOutputConnectors.filter((conn) => conn !== connID);

      newCanvasState = {
        ...newCanvasState,
        blocks: {
          ...newCanvasState.blocks,
          [otherBlockID]: {
            ...newCanvasState.blocks[otherBlockID],
            outputConnectors: otherOutputConnectors,
          },
        },
      };

      newCanvasState.connectors = deleteConnection(
        connID,
        newCanvasState.connectors
      );
    });
  }

  if (otherBlock.outputConnectors) {
    Array.from(otherBlock.outputConnectors).forEach((connID) => {
      const otherBlockID = newCanvasState.connectors[connID].outputBlockKey;

      let otherInputConnectors = [...newCanvasState.blocks[otherBlockID].inputConnectors || []]
      otherInputConnectors = otherInputConnectors.filter((conn) => conn !== connID);

      newCanvasState = {
        ...newCanvasState,
        blocks: {
          ...newCanvasState.blocks,
          [otherBlockID]: {
            ...newCanvasState.blocks[otherBlockID],
            inputConnectors: otherInputConnectors,
          },
        },
      };

      newCanvasState.connectors = deleteConnection(
        connID,
        newCanvasState.connectors
      );
    });
  }
  return newCanvasState;
};

export const {
  gameStart,
  gamePause,
  gameResume,
  gameTick,
  addBlock,
  connectBlock,
  blockMouseDown,
  blockMouseUp,
  canvasClick,
  blockMove,
  removeSelectedBlock,
  removeSelectedEdge,
  nodesChanged,
  edgesChanged,
  onConnect,
  nodeHandleClick,
  connectEnd,
  updateInitialRootIOPS,
  updateCurrentRootIOPS,
  updateTrafficPattern,
  updateTrafficRoutes,
  updateMinTPS,
  updateMaxTPS,
  renameBlock,
  openBlockSettings,
  closeBlockSettings,
  updateBlockDynamicProperties,
  resetGame,
  startProblem,
  leaveProblem,
  softResetGame,
  loadDesignState,
  setDraggingNode,
  setIopsConfigModalOpen,
  selectBlockForTour,
  setPaletteCollapsed,
} = gameSlice.actions;


export default gameSlice.reducer;
